import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, inject, Input, OnDestroy, PLATFORM_ID, Renderer2 } from '@angular/core';

import { EnvironmentService, IdUtils } from '@ecm/consumer';
import { EcmDispatcherModule, EnvUtils, IdZoneService } from '@ecm/dispatcher';

@Directive({
    selector: '[ecmPushTracker]',
    standalone: true,
    host: {
        '(click)': 'onClick()',
    },
})
export class MaterialPushTrackerDirective implements AfterViewInit, OnDestroy {
    private static readonly CLASS_NAME = 'MaterialPushTrackerDirective';
    private readonly renderer = inject(Renderer2);
    private readonly element = inject(ElementRef);
    private readonly environmentService = inject(EnvironmentService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly idZoneService = inject(IdZoneService);

    /* eslint-disable @angular-eslint/no-input-rename */
    @Input('pdb-zid') public pdb_zid: string;
    @Input('pdb-sc') public pdb_sc: string;
    @Input('pdb-idc') public pdb_idc: string;
    @Input('pdb-ta') public pdb_ta: string;
    @Input('pdb-vi') public pdb_vi: string;
    /* eslint-enable @angular-eslint/no-input-rename */

    private readonly trackingId: string = 'UA-82378354-6';

    private tracked = false;

    private readonly abortController = new AbortController();
    private observer: IntersectionObserver | null = null;

    public ngAfterViewInit() {
        if (EnvUtils.isEclair()) {
            return;
        }

        this.observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && document.visibilityState === 'visible') {
                    this.track('view');
                    this.observer?.disconnect();
                }
            },
            { threshold: 0 },
        );

        this.observer.observe(this.element.nativeElement);

        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.visibilityState === 'visible' && !this.tracked) {
                    this.observer?.disconnect();
                    this.observer?.observe(this.element.nativeElement);
                }
            },
            { signal: this.abortController.signal },
        );
    }

    public ngOnDestroy(): void {
        this.observer?.disconnect();
        this.abortController.abort();
    }

    private isTrackingDisabled(): boolean {
        const env = this.environmentService.instant();

        if (env.API.TRACKER.PIXEL_URL.includes('mocks-server')) {
            return false;
        }

        /**
         * Should get consent value from cookie "userconsent", values "c1" and "c2" must be set to 0
         */
        let isTrackingConsent = false;
        const consentCookie = document.cookie
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith('userconsent='));
        if (consentCookie) {
            const consentValue = consentCookie.substring('userconsent='.length);
            // to review consentValue is equal to {"c":{"w":{"cs":{"c1":"0","c2":"0","c3":"0","c4":"0","c5":"0","c6":"0"},"t":"2026-04-03T08:14:58.349Z","v":"2"}}}
            const consentOne = consentValue.split('"c1":"')[1]?.split('"')[0];
            const consentTwo = consentValue.split('"c2":"')[1]?.split('"')[0];
            isTrackingConsent = consentOne === '0' && consentTwo === '0';
        }

        return EnvUtils.isEclair() || !env.API.TRACKER.PIXEL_URL || !isTrackingConsent;
    }

    onClick(): void {
        this.track('click');
    }
    /**
     * Performs capping request through navigator.sendBeacon API if available, or tracking pixel
     * @param {string} eventType
     */
    private track(eventType: string): void {
        if (this.isTrackingDisabled()) {
            EcmDispatcherModule.sendConsoleInfo(MaterialPushTrackerDirective.CLASS_NAME, 'Tracking is disabled');
            return;
        }
        if (isPlatformBrowser(this.platformId)) {
            const env = this.environmentService.instant();
            if (env.API.TRACKER.PIXEL_URL) {
                const baseUrl: string = env.API.TRACKER.PIXEL_URL;
                const queryString: string = this.getTrackingQueryString(eventType);
                const url: string = baseUrl.concat(queryString);
                if (navigator?.sendBeacon) {
                    navigator.sendBeacon(url);
                } else {
                    this.insertTrackingPixel(url);
                }
                this.tracked = true;
            } else {
                EcmDispatcherModule.sendConsoleError(
                    MaterialPushTrackerDirective.CLASS_NAME,
                    "Couldn't perform tracking (env.TRACKER.PIXEL_URL key is false or undefined)",
                );
            }
        }
    }

    /**
     * Insert and remove a tracking pixel (1x1 px image) to perform capping request
     * @param {string} url
     */
    private insertTrackingPixel(url: string): void {
        EcmDispatcherModule.sendConsoleInfo(
            MaterialPushTrackerDirective.CLASS_NAME,
            'sendBeacon method is not available, will insert and remove tracking pixel instead',
        );
        const img: HTMLImageElement = new Image();
        img.width = 1;
        img.height = 1;
        img.onload = () => {
            this.renderer.removeChild(document.body, img);
        };
        img.src = url;
        this.renderer.appendChild(document.body, img);
    }

    /**
     * Generates query string for capping request
     * @param {string} eventType
     * @returns {string}
     */
    private getTrackingQueryString(eventType: string): string {
        const ts: number = Date.now();
        const pvuid: string = IdUtils.generateRandomId();
        // initialize with global variables
        let queryString: string =
            '?eventType=' +
            eventType +
            '&ts=' +
            ts.toString() +
            '&pvuid=' +
            pvuid +
            '&url=' +
            encodeURIComponent(window.location.href);

        // add inputs variables
        if (this.pdb_zid) {
            queryString += '&zid=' + encodeURIComponent(this.pdb_zid);
        }
        if (this.pdb_sc) {
            queryString += '&sc=' + encodeURIComponent(this.pdb_sc);
        }
        if (this.pdb_idc) {
            queryString += '&idc=' + encodeURIComponent(this.pdb_idc);
        }
        if (this.pdb_ta) {
            queryString += '&ta=' + encodeURIComponent(this.pdb_ta);
        }
        if (this.pdb_vi) {
            queryString += '&vi=' + encodeURIComponent(this.pdb_vi);
        }

        // add "must be defined" variables
        const gaProps = this.getGAProps();
        if (gaProps) {
            queryString += `&gaProps=${gaProps}`;
        }
        const gaClientId = this.getGaClientId();
        if (gaClientId) {
            queryString += `&gaClientId=${gaClientId}`;
        }
        const userUid = this.idZoneService.userExternalIdHash();
        if (userUid) {
            queryString += `&user_uid=${userUid}`;
        }
        const idpa = this.idZoneService.clientId() ?? '';
        if (idpa) {
            queryString += `&idpa=${idpa}`;
        }
        return queryString;
    }

    /**
     * @startregion GA Handlers
     */
    private getGAProps(): string {
        const result: string[] = [];
        const ga = (globalThis as any).ga;
        if (ga && typeof ga.getAll === 'function') {
            ga.getAll().forEach((tracker: any) => {
                result.push(tracker.get('trackingId'));
            });
        }
        return result.length > 0 ? result.join('|') : this.trackingId;
    }

    private getGaClientId(): string | null {
        const gaCookie = document.cookie
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith('_ga='));

        if (gaCookie) {
            // Format cookie _ga : GA1.1.XXXXXXXXXX.XXXXXXXXXX → on retire "GA1.1."
            return gaCookie.substring('_ga='.length + 6);
        }

        return null;
    }

    /**
     * @endregion
     */
}
